interface VideoModalProps {
  stream: any;
  onClose: () => void;
}

export default function VideoModal({ stream, onClose }: VideoModalProps) {
  const handleWatchLive = () => {
    // In production, this would use the actual streaming URL
    window.open('https://www.youtube.com/@sdanigeria', '_blank');
  };

  const handleFollowChurch = () => {
    // In production, this would link to the specific church's social media
    window.open('https://www.facebook.com/sdanigeria', '_blank');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/90 hover:bg-white rounded-full transition-colors cursor-pointer z-10 shadow-lg"
        >
          <i className="ri-close-line text-gray-700 text-xl"></i>
        </button>

        {/* Header */}
        <div className="relative h-56 bg-gradient-to-br from-emerald-600 to-teal-700 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40"></div>
          <img 
            src={stream.thumbnail}
            alt={stream.churchName}
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-6">
            <div className="flex items-center space-x-2 bg-red-600 px-3 py-1.5 rounded-full mb-4">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              <span className="text-sm font-bold">LIVE NOW</span>
            </div>
            <h2 className="text-2xl font-bold text-center mb-2" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
              {stream.churchName}
            </h2>
            <p className="text-base text-white/95 text-center font-semibold">{stream.service}</p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Event Description */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center space-x-2">
              <i className="ri-information-line text-emerald-600"></i>
              <span>About This Service</span>
            </h3>
            <p className="text-gray-700 text-sm leading-relaxed">
              Join us for a powerful worship experience as we gather together in praise and fellowship. 
              This service features inspiring messages from God's Word, uplifting music from our worship team, 
              and a time of prayer and communion with fellow believers. Whether you're joining us in person or 
              online, we're excited to worship together and grow in faith as a community.
            </p>
          </div>

          {/* Service Details */}
          <div className="mb-6 bg-gray-50 rounded-xl p-4">
            <h3 className="text-base font-bold text-gray-900 mb-3">Service Details</h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-3 text-gray-700">
                <i className="ri-map-pin-line text-emerald-600 mt-0.5 flex-shrink-0"></i>
                <div>
                  <p className="text-sm font-semibold">Location</p>
                  <p className="text-sm">{stream.location}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 text-gray-700">
                <i className="ri-time-line text-emerald-600 mt-0.5 flex-shrink-0"></i>
                <div>
                  <p className="text-sm font-semibold">Service Time</p>
                  <p className="text-sm">Started at {stream.startTime}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="mb-6 border-t border-gray-200 pt-6">
            <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center space-x-2">
              <i className="ri-contacts-line text-emerald-600"></i>
              <span>Contact Information</span>
            </h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-3 text-gray-700">
                <i className="ri-phone-line text-emerald-600"></i>
                <a href="tel:+2348012345678" className="text-sm hover:text-emerald-600 transition-colors">
                  +234 801 234 5678
                </a>
              </div>
              <div className="flex items-center space-x-3 text-gray-700">
                <i className="ri-mail-line text-emerald-600"></i>
                <a href="mailto:info@church.org" className="text-sm hover:text-emerald-600 transition-colors">
                  info@church.org
                </a>
              </div>
              <div className="flex items-center space-x-3 text-gray-700">
                <i className="ri-global-line text-emerald-600"></i>
                <a href="https://www.church.org" target="_blank" rel="noopener noreferrer" className="text-sm hover:text-emerald-600 transition-colors">
                  www.church.org
                </a>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleWatchLive}
              className="w-full flex items-center justify-center space-x-3 px-6 py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl cursor-pointer"
            >
              <i className="ri-play-circle-fill text-2xl"></i>
              <span>Watch Live Stream</span>
              <i className="ri-external-link-line text-sm"></i>
            </button>

            <button
              onClick={handleFollowChurch}
              className="w-full flex items-center justify-center space-x-3 px-6 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl cursor-pointer"
            >
              <i className="ri-facebook-circle-fill text-2xl"></i>
              <span>Follow Church on Social Media</span>
              <i className="ri-external-link-line text-sm"></i>
            </button>
          </div>

          <p className="text-xs text-gray-500 text-center mt-4">
            Click the buttons above to watch the livestream or connect with the church
          </p>
        </div>
      </div>
    </div>
  );
}
